import { ref, onUnmounted } from 'vue';

/* Тикающие «сейчас» для длительностей на экране.

   Один общий таймер на весь экран, а не по одному на карточку: при двадцати
   станках двадцать интервалов дают лишние перерисовки без всякой пользы. */
const now = ref(Date.now());
let timer = null;
let users = 0;

export function useNow(intervalMs = 1000) {
  users += 1;
  if (!timer) timer = setInterval(() => { now.value = Date.now(); }, intervalMs);

  onUnmounted(() => {
    users -= 1;
    if (users <= 0 && timer) { clearInterval(timer); timer = null; }
  });

  return now;
}

/* Длительность в человеческом виде. Секунды показываем только до минуты:
   на цеховом экране «2 ч 14 м» читается быстрее, чем «2:14:37». */
export function humanDuration(fromIso, to = Date.now()) {
  if (!fromIso) return '—';
  const sec = Math.max(0, Math.round((to - new Date(fromIso).getTime()) / 1000));
  if (sec < 60) return `${sec} с`;
  const m = Math.floor(sec / 60) % 60;
  const h = Math.floor(sec / 3600);
  if (h === 0) return `${m} мин`;
  if (h < 24) return `${h} ч ${String(m).padStart(2, '0')} м`;
  return `${Math.floor(h / 24)} сут ${h % 24} ч`;
}

export function humanSeconds(sec) {
  if (sec == null) return '—';
  const s = Math.max(0, Math.round(sec));
  if (s < 60) return `${s} с`;
  const m = Math.floor(s / 60) % 60;
  const h = Math.floor(s / 3600);
  return h ? `${h} ч ${String(m).padStart(2, '0')} м` : `${m} мин`;
}

export const pct = (v, digits = 0) => (v == null ? '—' : `${(v * 100).toFixed(digits)}%`);

export const num = (v, digits = 0) =>
  v == null ? '—' : Number(v).toLocaleString('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits });

/* Часовой пояс предприятия для всех отметок времени на экранах.

   Планшет в цехе может стоять с чужим поясом — привезли из другого филиала,
   поставили заводскую прошивку, кто-то поправил вручную. Смены при этом
   заданы настенным временем цеха, и сервер считает по поясу предприятия.
   Если экраны печатают время по поясу устройства, рядом оказываются два
   несовпадающих числа: «смена до 19:00» и «обновлено 21:03».

   Пояс ставится один раз при загрузке настроек. До этого форматируем
   по местному — это лучше, чем пустое место. */
let plantTz = null;
export const setPlantTimezone = (tz) => { plantTz = tz || null; };
const inPlantTz = (opts) => (plantTz ? { ...opts, timeZone: plantTz } : opts);

export const dateShort = (iso) => (iso
  ? new Date(iso).toLocaleString('ru-RU',
    inPlantTz({ day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }))
  : '—');

/* Только дата, без времени. Для даты смены: время в ней — артефакт
   хранения (полночь по поясу предприятия), и печатать «03:00» рядом
   с «смена А» значит показывать пользователю внутренности. */
export const dayShort = (iso) => (iso
  ? new Date(iso).toLocaleDateString('ru-RU',
    inPlantTz({ day: '2-digit', month: '2-digit' }))
  : '');

// Часы:минуты:секунды по поясу цеха — для отметок «обновлено» на экранах
export const timeShort = (d) => (d
  ? new Date(d).toLocaleTimeString('ru-RU',
    inPlantTz({ hour: '2-digit', minute: '2-digit', second: '2-digit' }))
  : '—');

/* Русское склонение при числительном: 1 день, 2 дня, 5 дней.

   Нужно там, где число подставляется в текст рядом с существительным.
   Без этого получается «2 дней», и интерфейс сразу выглядит переведённым
   с чужого языка. Формы задаются тройкой: [один, два, пять]. */
export function plural(n, forms) {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return forms[2];
  if (last > 1 && last < 5) return forms[1];
  if (last === 1) return forms[0];
  return forms[2];
}
